import { Popover } from "antd";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import usePaginationUrlControls from "hooks/usePaginationUrlControls";
import useScanColumns from "hooks/useScanColumns";
import useScanFilters from "hooks/useScanFilters";
import useTableCustomizationHelper, {
  DummyCustomizationHelper,
  TableCustomizationHelper,
} from "hooks/useTableCustomizationHelper";
import _, { cloneDeep } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ScanAPI from "restAPI/ScanAPI";
import { ExportField } from "types/api/exportField";
import { FindingsType } from "types/api/findingsType";
import {
  ScanStatus,
  ScanSummary,
  ScanSummaryListDTO,
} from "types/api/scanSummary";
import {
  DummyPaginationControlls,
  FilterInfo,
  FilterOptionInfo,
  PaginationControls,
} from "types/pagination";
import { KeyValue } from "types/tenantConfig";
import { getStartAndEndDateForQuickOption } from "utils/dateUtils";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import { ACFilterBarItemType } from "ac-components/ac-filter-bar/ACFilterBar";

import { DEFAULT_HIDDEN_COLUMN_KEYS } from "./constants";

type Props = {
  children?: JSX.Element[] | JSX.Element;
  toolName?: string;
  scanName?: string;
  triggerby?: typeof TRIGGER_BY_NAME_MAPPING;
  onScanUploadSuccess?: () => void;
};

type ScanListContextType = {
  paginationControls: PaginationControls;
  scanFilterColumns: ACFilterBarItemType[];
  filterValues: FilterOptionInfo;
  loading: boolean;
  dataSource: ScanSummary[];
  totalElements: number;
  tableCustomizationHelper: TableCustomizationHelper<ScanSummary>;
  activeColumns: ExportField[];
  allColumns: ACTableColumnType<ScanSummary>[];
  actionRender?: (record: ScanSummary) => React.ReactNode;
  actualAppliedFilters: FilterInfo;
  // columnTitleRenderers: ColumnTitleRenderers<ScanSummary>;
  onRowSelect: (selectedRowKeys: React.Key[]) => void;
  selectedRowKeys: React.Key[];
  refresh: () => void;
};

const defaultValue: ScanListContextType = {
  paginationControls: DummyPaginationControlls,
  scanFilterColumns: [],
  filterValues: {},
  loading: true,
  dataSource: [],
  totalElements: 0,
  tableCustomizationHelper: DummyCustomizationHelper,
  activeColumns: [],
  allColumns: [],
  actualAppliedFilters: {},
  // columnTitleRenderers: {},
  selectedRowKeys: [],
  onRowSelect: () => {},
  refresh: () => {},
};

const ScanListContext = React.createContext<ScanListContextType>(defaultValue);

export const ERROR_TITLE = "Scan failed :";

export const TRIGGER_BY_NAME_MAPPING = {
  UI_UPLOAD: "UI Upload",
};

export const SCAN_FINDING_LIST = ["DUPLICATE", "RESOLVED", "NEW", "TOTAL"];

export default function ScanListProvider({
  toolName,
  scanName,
  triggerby,
  children,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<ScanSummary[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const cancelRef = useRef(_.noop);
  const { globalFilterHash } = useGlobalFilterContext();
  const globalFilterHashRef = useRef(globalFilterHash);

  const {
    scanFilterColumns,
    filterValues,
    refreshFilters: refreshScanFilters,
  } = useScanFilters(true) as unknown as {
    scanFilterColumns: ACFilterBarItemType[];
    filterValues: FilterOptionInfo;
    refreshFilters(): void;
  };

  const paginationControls = usePaginationUrlControls({
    defaultPageSize: 10,
    defaultSortInfo: { createdAt: "descend" },
  });

  const {
    page,
    pageSize,
    filterInfo,
    onFilterChange,
    resetFilters,
    sortString,
  } = paginationControls;

  const actualAppliedFilters = useDetectFilterChange(filterInfo);

  const fetchData = useCallback(() => {
    cancelRef.current();
    setLoading(true);

    const findingCounts = actualAppliedFilters?.findings as
      | FindingsType[]
      | undefined;
    // Generate queryparams object
    const filters = cloneDeep(actualAppliedFilters);

    if (filters?.id?.[0]) {
      const id = filters.id[0] as string;
      if (isNaN(Number(id))) {
        if (id.match(/s-/gi)) {
          filters.id[0] = id.split("-").pop() as string;
        } else if (id === "s" || id === "S") {
          filters.id[0] = "";
        }
        if (filters.id[0] !== "" && isNaN(Number(filters.id[0]))) {
          filters.id[0] = -1;
        }
      }
    }
    if (filters?.createdAt && filters.createdAt.length === 1) {
      filters.createdAt = getStartAndEndDateForQuickOption({
        label: filters.createdAt[0],
        utcString: true,
      }) as string[];
    }

    if (filters?.scanDate && filters.scanDate.length === 1) {
      filters.scanDate = getStartAndEndDateForQuickOption({
        label: filters.scanDate[0],
        utcString: true,
      }) as string[];
    }

    if (filters?.tags) {
      filters.tags = _.map(filters.tags, (tag) => {
        const { key, value } = tag as KeyValue;
        let keyValue = key;
        if (value) {
          keyValue = `${key}:${value}`;
        }
        return keyValue;
      });
    }
    const [key, order] = (sortString || "").split(",");
    const obj = {
      ...filters,
      page: page - 1,
      size: pageSize,
      sort: [`${key},${order === "descend" ? "desc" : "asc"}`],
      findingCounts,
      ..._.omit(filters, ["filters", "sortColumn", "sortOrder"]),
    };

    if (filters?.mostRecentScans) {
      // @ts-expect-error - ype 'string' is not assignable to type 'FilterValue[]'
      obj.mostRecentScans = filters.mostRecentScans[0];
    }

    // @ts-expect-error
    if (!_.isEmpty(toolName)) obj["tool"] = scanName || toolName;
    // @ts-expect-error
    if (!_.isEmpty(triggerby)) obj["triggerby"] = triggerby;

    // @ts-expect-error
    delete obj.findings;

    const { promise, cancel } = ScanAPI.getAll(obj);
    promise
      .then((response: ScanSummaryListDTO) => {
        setTotalElements(response.totalElements!);
        setDataSource(response.content!);
      })
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => {
        setLoading(false);
      });

    cancelRef.current = cancel;
    return cancel;
  }, [
    actualAppliedFilters,
    page,
    pageSize,
    sortString,
    toolName,
    scanName,
    triggerby,
  ]);

  const refresh = useCallback(() => {
    // window.dispatchEvent(new Event("refreshFindings"));
    fetchData();
    refreshScanFilters();
  }, [fetchData, refreshScanFilters]);

  const { allColumns, actionRender } = useScanColumns({
    refresh,
    filterInfo,
    onFilterChange,
  });

  const tableCustomizationHelper = useTableCustomizationHelper({
    columns: allColumns,
    draggable: true,
    alwaysVisibleKeys: ["id"],
    defaultHiddenKeys: DEFAULT_HIDDEN_COLUMN_KEYS,
    prefKey: "scan",
  });
  const { exportColumns } = tableCustomizationHelper;

  const activeColumns = exportColumns;

  useEffect(() => fetchData(), [fetchData]);

  useEffect(() => {
    if (globalFilterHashRef.current !== globalFilterHash) {
      globalFilterHashRef.current = globalFilterHash;
      const filterChanged = resetFilters();
      if (!filterChanged) {
        refresh();
      }
    }
  }, [globalFilterHash, resetFilters, refresh]);

  useEffect(() => {
    return () => {
      cancelRef.current();
    };
  }, []);

  const value = useMemo<ScanListContextType>(() => {
    return {
      paginationControls,
      scanFilterColumns,
      filterValues,
      loading,
      dataSource,
      totalElements,
      allColumns,
      actionRender,
      actualAppliedFilters,
      tableCustomizationHelper,
      selectedRowKeys,
      activeColumns,
      onRowSelect: setSelectedRowKeys,
      refresh,
    };
  }, [
    tableCustomizationHelper,
    paginationControls,
    scanFilterColumns,
    filterValues,
    loading,
    dataSource,
    totalElements,
    allColumns,
    actionRender,
    actualAppliedFilters,
    selectedRowKeys,
    activeColumns,
    setSelectedRowKeys,
    refresh,
  ]);

  return (
    <ScanListContext.Provider value={value}>
      {children}
    </ScanListContext.Provider>
  );
}

export const useScanListContext = () => {
  return useContext(ScanListContext);
};

export enum SCAN_STATUS_COLORS {
  INITIATED = "Blue",
  COMPLETED = "Success",
  PROCESSING = "Primary",
  IMPORTING = "Info",
  FAILED = "Error",
}

type ScanProgressIndicatorProps = {
  record: ScanSummary;
  onChange(): void;
};
export function ScanProgressIndicator({
  record,
  onChange,
}: ScanProgressIndicatorProps) {
  useEffect(() => {
    if (
      ![ScanStatus.COMPLETED, ScanStatus.FAILED].includes(record.scanStatus)
    ) {
      const intervalId = startProgressCheckInterval(record, onChange);
      return () => clearInterval(intervalId);
    }
  }, [record, onChange]);

  const { imageName, progress } = getProgress(record.scanStatus);

  return (
    <div className="progressContainer">
      {imageName === "Failed.png" ? (
        <Popover
          overlayStyle={{ maxWidth: 600 }}
          content={
            _.capitalize(record.errorMessage?.replace(ERROR_TITLE, "")) ??
            "No info available!"
          }
          placement="topRight"
          trigger="hover"
          title={ERROR_TITLE}
        >
          <img
            src={`images/${imageName}`}
            alt="Scan Status"
            className="progress"
          />
        </Popover>
      ) : (
        <img
          src={`images/${imageName}`}
          alt="Scan Status"
          className="progress"
        />
      )}
      <div className="progressText">
        {progress <= 66 && progress !== -1 ? (
          <p style={{ fontSize: 12 }}>{`${progress}%`}</p>
        ) : null}
      </div>
    </div>
  );
}

function startProgressCheckInterval(record: ScanSummary, onChange: () => void) {
  const intervalId = setInterval(async () => {
    try {
      const params = {
        id: record.id != null ? [String(record.id)] : undefined,
      };
      const { content } = await ScanAPI.getAll(params).promise;
      // since the API is for search, may return other scans as well
      const scan = _.find(content, { id: record.id });
      if (scan && scan.scanStatus !== record.scanStatus) {
        if (
          [ScanStatus.COMPLETED, ScanStatus.FAILED].includes(scan.scanStatus)
        ) {
          clearInterval(intervalId);
        }
        onChange();
      }
    } catch (e) {
      console.log(e);
    }
  }, 10000);
  return intervalId;
}

export function getProgress(value: ScanStatus) {
  let progress = 0;
  let imageName = "purple_loader.gif";
  switch (value) {
    case ScanStatus.INITIATED:
      progress = 33;
      break;
    case ScanStatus.PROCESSING:
      progress = 66;
      break;
    case ScanStatus.COMPLETED:
      progress = 100;
      imageName = "Finished.png";
      break;
    case ScanStatus.FAILED:
      progress = -1;
      imageName = "Failed.png";
      break;
    default:
      progress = -1;
  }
  return { imageName, progress };
}
